import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

export interface HourlyForecast {
  timestamp: string;
  temperature: number;
  cloud_cover: number;
  precipitation: number;
  humidity: number;
  wind_speed: number;
  description: string;
  icon: string;
}

export interface SunTimes {
  sunrise: string;
  sunset: string;
  day_length_hours: number;
}

export interface WeatherData {
  location: {
    lat: number;
    lon: number;
    name: string | null;
  };
  current: {
    timestamp: string;
    temperature: number;
    cloud_cover: number;
    precipitation: number;
    humidity: number;
    wind_speed: number;
    description: string;
    icon: string;
  };
  hourly: HourlyForecast[];
  sun_times: SunTimes | null;
  cached: boolean;
  cache_expires: string | null;
}

interface UseWeatherResult {
  weather: WeatherData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  lastUpdated: Date | null;
}

export function useWeather(lat: number, lon: number, refreshInterval = 600000): UseWeatherResult {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchWeather = useCallback(async () => {
    if (!lat || !lon) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get<WeatherData>('/api/weather', {
        params: { lat, lon }
      });
      setWeather(response.data);
      setLastUpdated(new Date());
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch weather';
      setError(message);
      console.error('Weather fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [lat, lon]);

  useEffect(() => {
    fetchWeather();
    
    // Set up auto-refresh
    const interval = setInterval(fetchWeather, refreshInterval);
    
    return () => clearInterval(interval);
  }, [fetchWeather, refreshInterval]);

  return {
    weather,
    loading,
    error,
    refetch: fetchWeather,
    lastUpdated,
  };
}

